import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { GroupLeaderResponse } from "@/utils/validation";

const defaultRatingValue = "Varken eller";

export const useGroupLeaderSurvey = (meetingNumber: string) => {
  console.log('🚀 useGroupLeaderSurvey hook called with meetingNumber:', meetingNumber);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [existingResponseId, setExistingResponseId] = useState<string | null>(null);

  const [formData, setFormData] = useState<GroupLeaderResponse>({
    date: new Date().toISOString().split('T')[0],
    meeting_number: parseInt(meetingNumber || "1"),
    participants_count: 0,
    completed_all_moments: defaultRatingValue,
    missing_moments: "",
    parent_engagement: defaultRatingValue,
    engaged_moments: [],
    supportive_participants: defaultRatingValue,
    good_delivery: defaultRatingValue,
    difficult_moments: [],
    sufficient_knowledge: defaultRatingValue,
    missing_knowledge: "",
    confirmed_participants: defaultRatingValue,
    activated_group: defaultRatingValue,
    maintained_structure: defaultRatingValue,
    group_leader_id: "",
    other_comments: "",
  });

  // Fetch existing response for this meeting number - using immediate execution instead of useEffect
  console.log('⚡ About to create useEffect for meetingNumber:', meetingNumber);
  
  // Initialize loading and fetch data immediately
  useMemo(() => {
    console.log('🎯 useMemo triggered - fetching data immediately');
    const fetchExistingResponse = async () => {
      try {
        console.log('🔍 Fetching existing response for meeting number:', meetingNumber);
        
        const { data: { session } } = await supabase.auth.getSession();
        console.log('👤 Current session user ID:', session?.user?.id);
        
        if (!session?.user?.id) {
          console.log('❌ No authenticated user found');
          setIsLoading(false);
          return;
        }

        console.log('🔎 Querying group_leader_responses with:', {
          group_leader_id: session.user.id,
          meeting_number: parseInt(meetingNumber || "1")
        });

        const { data, error } = await supabase
          .from("group_leader_responses")
          .select("*")
          .eq("group_leader_id", session.user.id)
          .eq("meeting_number", parseInt(meetingNumber || "1"))
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        console.log('📊 Query result:', { data, error });

        if (error) {
          console.error("❌ Error fetching existing response:", error);
        } else if (data) {
          console.log('✅ Found existing response, populating form:', data);
          // Populate form with existing data
          setExistingResponseId(data.id);
          setFormData({
            date: data.date,
            meeting_number: data.meeting_number,
            participants_count: data.participants_count,
            completed_all_moments: data.completed_all_moments,
            missing_moments: data.missing_moments || "",
            parent_engagement: data.parent_engagement,
            engaged_moments: data.engaged_moments || [],
            supportive_participants: data.supportive_participants,
            good_delivery: data.good_delivery,
            difficult_moments: data.difficult_moments || [],
            sufficient_knowledge: data.sufficient_knowledge,
            missing_knowledge: data.missing_knowledge || "",
            confirmed_participants: data.confirmed_participants,
            activated_group: data.activated_group,
            maintained_structure: data.maintained_structure,
            group_leader_id: data.group_leader_id,
            other_comments: data.other_comments || "",
          });
          console.log('📝 Form data updated with existing response');
        } else {
          console.log('📄 No existing response found for this meeting number');
        }
      } catch (error) {
        console.error("💥 Error fetching existing response:", error);
      } finally {
        console.log('✅ Loading complete');
        setIsLoading(false);
      }
    };

    fetchExistingResponse();
    return null; // useMemo must return a value
  }, [meetingNumber]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      
      console.log('Initial form data:', formData);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error("No authenticated user found");
      }

      // Create the insert data with all required fields
      const insertData = {
        ...formData,
        group_leader_id: session.user.id,
        difficult_moments: formData.difficult_moments || [],
        engaged_moments: formData.engaged_moments,
        missing_moments: formData.missing_moments || "",
        missing_knowledge: formData.missing_knowledge || "",
        meeting_number: formData.meeting_number,
        date: formData.date,
        participants_count: formData.participants_count,
        completed_all_moments: formData.completed_all_moments,
        parent_engagement: formData.parent_engagement,
        supportive_participants: formData.supportive_participants,
        good_delivery: formData.good_delivery,
        sufficient_knowledge: formData.sufficient_knowledge,
        confirmed_participants: formData.confirmed_participants,
        activated_group: formData.activated_group,
        maintained_structure: formData.maintained_structure
      };

      console.log('Prepared insert data:', insertData);

      // Use upsert or update if we have an existing response
      if (existingResponseId) {
        const { error: updateError } = await supabase
          .from("group_leader_responses")
          .update(insertData)
          .eq("id", existingResponseId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("group_leader_responses")
          .insert(insertData);

        if (insertError) throw insertError;
      }

      toast({
        title: "Framgång",
        description: "Din utvärdering har sparats",
      });

      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Error submitting survey:", error);
      toast({
        title: "Fel",
        description: "Det gick inte att spara utvärderingen. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof GroupLeaderResponse, value: any) => {
    console.log(`Input change - Field: ${field}, Value:`, value);
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      console.log('Updated form data:', newData);
      return newData;
    });
  };

  return {
    formData,
    isSubmitting,
    isLoading,
    handleInputChange,
    handleSubmit,
  };
};